import { CARD_ASPECT } from './styled';
import { GEM_SHAPE } from './gemShape';

export { GEM_SHAPE };

/** Each damage counter is 10 damage, as everyone counted the glass gems that came with the game. */
export const DAMAGE_PER_GEM = 10;

/**
 * A gem's width against the card's: flat glass gems like the game's are about 18 mm across, the card 63 mm wide.
 */
export const GEM_SIZE = 0.28;

/** Where a Pokémon card's artwork is, as fractions of the card: gems go there first. */
const ARTWORK = { left: 0.1, right: 0.9, top: 0.12, bottom: 0.48 };
const WHOLE_CARD = { left: 0.06, right: 0.94, top: 0.05, bottom: 0.95 };

/** How many random places to try for a gem before sweeping the card for one. */
const TRIES = 120;

/** How finely the card is swept for a free spot, in steps across and down. */
const SWEEP = 40;

/** A small seeded random number generator (mulberry32): the same seed gives the same numbers, every time. */
function random(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** A number for a string, to seed with ("active-66749"). */
export function seedOf(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  }
  return hash >>> 0;
}


/**
 * How close a new gem may sit to the ones already down, centre to centre against a gem's size, loosest last: with a
 * gap while there's room, then just touching, then nudged a little over its neighbours, as on a well-hit real card.
 */
const SPACINGS = [1.06, 1.0, 0.85, 0.72];

/** A spot for a gem in `area`, chosen by `next`. */
const spotIn = (area, across, down, next) => ({
  x: area.left + across / 2 + next() * (area.right - area.left - across),
  y: area.top + down / 2 + next() * (area.bottom - area.top - down),
});

/** Spots across the whole card, top to bottom, for what random tries missed. */
function sweep(across, down) {
  const spots = [];
  for (let row = 0; row <= SWEEP; row++) {
    for (let col = 0; col <= SWEEP; col++) {
      spots.push({
        x: WHOLE_CARD.left + across / 2 + (col / SWEEP) * (WHOLE_CARD.right - WHOLE_CARD.left - across),
        y: WHOLE_CARD.top + down / 2 + (row / SWEEP) * (WHOLE_CARD.bottom - WHOLE_CARD.top - down),
      });
    }
  }
  return spots;
}

/**
 * Where a Pokémon's damage counters sit: one glass gem per 10 damage, scattered like a handful dropped on the card,
 * over the artwork first and onto the rest of the card once the artwork is full. A gem keeps a gap from the others
 * while there's room, and only on a card too full for that does it touch them (SPACINGS). Each gem's place and colour
 * come from the seed and its own number, so a gem stays where it landed as more are added, and the same Pokémon looks
 * the same every time it's drawn.
 *
 * @param {number} damage
 * @param {number} seed for this Pokémon in play (seedOf)
 * @returns {{ size: number, gems: { x: number, y: number, color: 'amber'|'blue' }[] }} the gems' width against the
 *   card's, and each gem's middle as fractions of the card's width and height
 */
export function gemLayout(damage, seed) {
  const count = Math.max(0, Math.floor(damage / DAMAGE_PER_GEM));
  const across = GEM_SIZE;
  const down = (GEM_SIZE / GEM_SHAPE) * CARD_ASPECT;
  const swept = sweep(across, down);
  const gems = [];
  const distance = (gem, spot) => Math.hypot((gem.x - spot.x) / across, (gem.y - spot.y) / down);
  const room = (spot) => Math.min(Infinity, ...gems.map(gem => distance(gem, spot)));

  for (let i = 0; i < count; i++) {
    const next = random(seed + i * 7919);
    const color = next() < 0.5 ? 'amber' : 'blue';
    const tries = [ARTWORK, WHOLE_CARD].flatMap(area => Array.from({ length: TRIES }, () => spotIn(area, across, down, next)));
    let spot = null;
    for (const spacing of SPACINGS) {
      spot = tries.find(candidate => room(candidate) >= spacing) || swept.find(candidate => room(candidate) >= spacing);
      if (spot) {
        break;
      }
    }
    // Fuller than any card gets in play (a Pokémon is knocked out first): the roomiest spot there is.
    if (!spot) {
      spot = swept.reduce((best, candidate) => (room(candidate) > room(best) ? candidate : best));
    }
    gems.push({ ...spot, color });
  }

  return { size: GEM_SIZE, gems };
}

/**
 * The seed for a Pokémon in play, so its gems are the same on the board and in its menu: the active spot's row, or
 * the benched card's place on the bench.
 */
export const gemSeed = {
  active: (activeId) => seedOf(`active-${activeId}`),
  bench: (pivotId) => seedOf(`bench-${pivotId}`),
};
